import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import {
  EditorContent,
  useEditor,
  ReactNodeViewRenderer,
  Editor,
} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import type { ImageOptions } from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import FontFamily from "@tiptap/extension-font-family";
import { ResizableImageNodeView } from "./components/ResizableImageNodeView";
import { Plugin } from "prosemirror-state";
import { Extension, CommandProps, getMarkRange } from "@tiptap/core";
import { createPortal } from "react-dom";
import { Placeholder } from "@tiptap/extensions";
import Mention from "@tiptap/extension-mention";
import { visibleScrollbar } from "../../utils/functions/get-visible-scrollbar";
import suggestions from "./utils/functions/get-suggestions-options";
import CharacterCount from "@tiptap/extension-character-count";
import { Attachment, RichTextEditorProps } from "./utils/types/types";

declare module "@tiptap/extension-image" {
  interface ImageOptions {
    imgMaxWidth: string;
  }
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    fontSize: {
      setFontSize: (fontSize: string) => ReturnType;
      unsetFontSize: () => ReturnType;
    };
    highlight: {
      setHighlight: (attributes?: { color: string }) => ReturnType;
      toggleHighlight: (attributes?: { color: string }) => ReturnType;
      unsetHighlight: () => ReturnType;
    };
    textIndent: {
      indent: () => ReturnType;
      outdent: () => ReturnType;
    };
  }
}

const TextIndent = Extension.create({
  name: "textIndent",

  addGlobalAttributes() {
    return [
      {
        types: ["paragraph", "heading", "bulletList", "orderedList", "image"],
        attributes: {
          indentLevel: {
            default: 0,
            parseHTML: (element) => {
              const level = element.getAttribute("data-indent-level");
              return level ? parseInt(level, 10) : 0;
            },
            renderHTML: (attributes: { indentLevel?: number }) => {
              if (!attributes.indentLevel || attributes.indentLevel === 0) {
                return {};
              }
              return {
                "data-indent-level": attributes.indentLevel,
                style: `margin-left: ${attributes.indentLevel * 1.5}rem;`,
              };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      indent:
        () =>
        ({ chain, state }: CommandProps) => {
          const { selection } = state;
          const { $from } = selection;

          const currentNode = $from.node();
          const currentLevel = currentNode.attrs.indentLevel || 0;

          if (currentLevel >= 50) return false;

          return chain()
            .updateAttributes(currentNode.type.name, {
              indentLevel: currentLevel + 1,
            })
            .run();
        },
      outdent:
        () =>
        ({ chain, state }: CommandProps) => {
          const { selection } = state;
          const { $from } = selection;

          const currentNode = $from.node();
          const currentLevel = currentNode.attrs.indentLevel || 0;

          if (currentLevel <= 0) return false;

          return chain()
            .updateAttributes(currentNode.type.name, {
              indentLevel: currentLevel - 1,
            })
            .run();
        },
    };
  },
});

const FontSize = Extension.create({
  name: "fontSize",
  addOptions() {
    return {
      types: ["textStyle"],
    };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element: HTMLElement) =>
              element.style.fontSize.replace(/['"]+/g, ""),
            renderHTML: (attributes: { fontSize?: string | null }) => {
              if (!attributes.fontSize) {
                return {};
              }
              return {
                style: `font-size: ${attributes.fontSize}`,
              };
            },
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setFontSize:
        (fontSize: string) =>
        ({ chain }: CommandProps) => {
          return chain().setMark("textStyle", { fontSize }).run();
        },
      unsetFontSize:
        () =>
        ({ chain }: CommandProps) => {
          return chain()
            .setMark("textStyle", { fontSize: null })
            .removeEmptyTextStyle()
            .run();
        },
    };
  },
});

const CustomLink = Link.extend({
  inclusive: false,
  addProseMirrorPlugins() {
    const plugins: Plugin[] = [];

    const preventLinkExtensionPlugin = new Plugin({
      appendTransaction: (transactions, oldState, newState) => {
        const { doc, tr } = newState;
        let modified = false;

        if (!transactions.some((transaction) => transaction.docChanged)) {
          return null;
        }
        doc.descendants((node, pos) => {
          if (node.isText) {
            const marks = node.marks.filter(
              (mark) => mark.type.name === "link"
            );
            if (marks.length > 0) {
              const text = node.text || "";
              let start = 0;
              let end = text.length;

              while (start < text.length && /\s/.test(text[start])) {
                start++;
              }
              while (end > start && /\s/.test(text[end - 1])) {
                end--;
              }

              if (start > 0 || end < text.length) {
                const linkMark = marks[0];
                const linkAttrs = linkMark.attrs;

                tr.removeMark(pos, pos + text.length, linkMark.type);
                if (start < end) {
                  tr.addMark(
                    pos + start,
                    pos + end,
                    linkMark.type.create(linkAttrs)
                  );
                }

                modified = true;
              }
            }
          }
        });

        return modified ? tr : null;
      },
    });

    return [...plugins, preventLinkExtensionPlugin];
  },
});

const CustomImage = Image.configure({
  HTMLAttributes: {
    class: "resizable-image",
  },
  allowBase64: true,
  inline: true,
}).extend({
  addOptions(): ImageOptions {
    const parent = this.parent!();
    return {
      ...parent,
      imgMaxWidth: "100%",
    };
  },
  addAttributes() {
    return {
      ...this.parent?.(),
      src: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute("src"),
        renderHTML: (attributes: { src?: string | null }) => {
          if (!attributes.src) return {};
          return { src: attributes.src };
        },
      },
      alt: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute("alt"),
        renderHTML: (attributes: { alt?: string | null }) => {
          if (!attributes.alt) return {};
          return { alt: attributes.alt };
        },
      },
      width: {
        default: "100%",
        parseHTML: (element: HTMLElement) =>
          element.getAttribute("width") || element.style.width || null,
        renderHTML: (attributes: { width?: string | null }) => {
          if (!attributes.width) return {};
          return { width: attributes.width };
        },
      },
      textAlign: {
        default: null,
        parseHTML: (element: HTMLElement) =>
          element.getAttribute("data-text-align") ||
          element.style.textAlign ||
          null,
        renderHTML: (attributes: { textAlign?: string | null }) => {
          if (!attributes.textAlign) return {};
          return {
            "data-text-align": attributes.textAlign,
            style: `text-align: ${attributes.textAlign}`,
          };
        },
      },
      indentLevel: {
        default: 0,
        parseHTML: (element: HTMLElement) => {
          const level = element.getAttribute("data-indent-level");
          return level ? parseInt(level, 10) : 0;
        },
        renderHTML: (attributes: { indentLevel?: number }) => {
          if (!attributes.indentLevel || attributes.indentLevel === 0)
            return {};
          return {
            "data-indent-level": attributes.indentLevel,
            style: `margin-left: ${attributes.indentLevel * 1.5}rem;`,
          };
        },
      },
    };
  },
  addNodeView() {
    const imgMaxWidth = this.options.imgMaxWidth;
    return ReactNodeViewRenderer((props) => (
      <ResizableImageNodeView {...props} imgMaxWidth={imgMaxWidth} />
    ));
  },
});

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  message,
  onMessageChange,
  onEditorReady,
  onAttachmentsChange,
  onAttachmentsUpdate,
  placeholder,
  placeholderFontSize = "12px",
  mentionActiveUsersList,
  setTaggedUsers,
  handleKeyDown,
  disableUnderline = false,
  disableItalic = false,
  disableStrikethrough = false,
  disableImageSupport = false,
  editable: editableProp = true,
  onImageFile,
  imgMaxWidth,
  charLimit,
}) => {
  const [editorHTML, setEditorHTML] = useState(message || "");
  const [isEditingLink, setIsEditingLink] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkText, setLinkText] = useState("");
  const [isLinkBubbleForcedHidden, setIsLinkBubbleForcedHidden] =
    useState(false);
  const currentAttachmentsRef = useRef<Attachment[]>([]);
  const [linkUrlError, setLinkUrlError] = useState<string>("");
  const [editMenuPosition, setEditMenuPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [hasLists, setHasLists] = useState(false);
  const [linkBubble, setLinkBubble] = useState<{
    href: string;
    top?: number;
    bottom?: number;
    startLeft: number;
    endRight: number;
  } | null>(null);
  const [linkBubbleWidth, setLinkBubbleWidth] = useState<number | null>(null);

  const bubbleMenuRef = useRef<HTMLDivElement>(null);
  // The link selection captured when the edit-link popup opens. Clicking the
  // popup inputs blurs the editor and drifts its live selection, so we anchor
  // positioning/apply/remove to this saved range instead.
  const savedLinkSelectionRef = useRef<{ from: number; to: number } | null>(
    null
  );
  const editorContainerRef = useRef<HTMLDivElement>(null);

  const measureLinkBubble = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      setLinkBubbleWidth(node.getBoundingClientRect().width);
    }
  }, []);
  const onAttachmentsChangeRef = useRef(onAttachmentsChange);
  const onImageFileRef = useRef(onImageFile);
  useEffect(() => {
    onImageFileRef.current = onImageFile;
  }, [onImageFile]);

  const useOnClickOutside = (
    ref: React.RefObject<HTMLDivElement>,
    handler: (event: MouseEvent | TouchEvent) => void
  ) => {
    useEffect(() => {
      const listener = (event: MouseEvent | TouchEvent) => {
        if (!ref.current || ref.current.contains(event.target as Node)) {
          return;
        }
        handler(event);
      };
      document.addEventListener("mousedown", listener);
      document.addEventListener("touchstart", listener);
      return () => {
        document.removeEventListener("mousedown", listener);
        document.removeEventListener("touchstart", listener);
      };
    }, [ref, handler]);
  };

  useOnClickOutside(bubbleMenuRef, (event) => {
    const target = event.target as Element | null;
    if (target?.closest?.("[data-link-bubble]")) {
      return;
    }
    setIsEditingLink(false);
  });

  const checkForLists = useCallback((editor: Editor) => {
    const { doc } = editor.state;
    let hasListNodes = false;

    doc.descendants((node) => {
      if (node.type.name === "bulletList" || node.type.name === "orderedList") {
        hasListNodes = true;
        return false;
      }
    });

    setHasLists(hasListNodes);
  }, []);

  const extractTaggedUsers = useCallback((editor: Editor): string[] => {
    const { doc } = editor.state;
    const taggedUserIds: string[] = [];

    doc.descendants((node) => {
      if (node.type.name === "mention") {
        const userId = node.attrs.id;
        if (userId && !taggedUserIds.includes(userId)) {
          taggedUserIds.push(userId);
        }
      }
    });

    return taggedUserIds;
  }, []);

  const updateTaggedUsers = useCallback(
    (editor: Editor) => {
      if (setTaggedUsers) {
        const taggedUserIds = extractTaggedUsers(editor);
        setTaggedUsers(taggedUserIds);
      }
    },
    [setTaggedUsers, extractTaggedUsers]
  );

  const extensions = useMemo(
    () => [
      StarterKit.configure({
        link: false,
        underline: false,
        ...(disableItalic && { italic: false }),
        ...(disableStrikethrough && { strike: false }),
      }),
      CustomLink.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: {
          class:
            "text-verba-blue dark:text-verba-blue-dk underline cursor-pointer",
        },
      }),
      ...(disableImageSupport
        ? []
        : [
            CustomImage.configure({
              HTMLAttributes: {
                class: "resizable-image",
              },
              allowBase64: true,
              imgMaxWidth: imgMaxWidth ?? "100%",
            }),
          ]),
      ...(disableUnderline ? [] : [Underline.configure()]),
      Placeholder.configure({
        placeholder: placeholder || "Start typing here...",
        emptyEditorClass: "is-editor-empty",
      }),
      TextAlign.configure({
        types: disableImageSupport
          ? ["heading", "paragraph"]
          : ["heading", "paragraph", "image"],
      }),
      TextStyle.configure(),
      FontFamily.configure(),
      Color.configure(),
      FontSize.configure(),
      TextIndent.configure(),
      ...(charLimit != null
        ? [CharacterCount.configure({ limit: charLimit })]
        : []),
      ...(mentionActiveUsersList
        ? [
            Mention.configure({
              HTMLAttributes: {
                class:
                  "text-sm inline-flex bg-tagged-user-bg dark:bg-tagged-user-bg-dk border border-card-yellow-stroke dark:border-card-yellow-stroke-dk rounded-[4px] text-tagged-user dark:text-tagged-user-dk font-medium px-[6px] py-[2px] mr-1 after:content-['\u200B'] whitespace-nowrap",
              },
              suggestion: suggestions(mentionActiveUsersList),
            }),
          ]
        : []),
    ],
    [
      disableImageSupport,
      disableUnderline,
      disableItalic,
      disableStrikethrough,
      mentionActiveUsersList,
      placeholder,
      imgMaxWidth,
      charLimit,
    ]
  );

  const editor = useEditor(
    {
      immediatelyRender: false,
      editable: editableProp,
      extensions,
      editorProps: {
        attributes: {
          class: "flex-grow focus:outline-none break-words",
        },
        handleKeyDown: (view, event) => {
          if (event.key === "Backspace" && editor?.isActive("link")) {
            setIsLinkBubbleForcedHidden(true);
          }
          // if (handleKeyDown) {
          //   handleKeyDown(
          //     event as unknown as React.KeyboardEvent<
          //       HTMLInputElement | HTMLTextAreaElement
          //     >
          //   );
          // }
          return false;
        },
        handleDrop: (_view, event, _slice, moved) => {
          if (moved || !editor) return false;

          const files = event.dataTransfer?.files;
          if (files && files.length > 0) {
            const imageFiles = Array.from(files).filter((file) =>
              file.type.startsWith("image/")
            );

            if (disableImageSupport && imageFiles.length > 0) {
              event.preventDefault();
              return true;
            }

            if (imageFiles.length > 0) {
              event.preventDefault();
              // If the caller supplies onImageFile, delegate entirely — this
              // prevents the outer container's onDrop from also firing (double
              // insertion) because we stop propagation here.
              if (onImageFileRef.current) {
                event.stopPropagation();
                imageFiles.forEach((file) => onImageFileRef.current!(file));
                return true;
              }

              const coordinates = editor.view.posAtCoords({
                left: event.clientX,
                top: event.clientY,
              });

              imageFiles.forEach((file) => {
                const reader = new FileReader();
                reader.onload = (e) => {
                  if (!e.target?.result) return;
                  const dataUrl = e.target.result as string;
                  const img = new window.Image();
                  img.src = dataUrl;
                  img.onload = () => {
                    const { clientWidth } = editor.view.dom;
                    const { naturalWidth } = img;
                    const width =
                      naturalWidth > clientWidth ? "100%" : `${naturalWidth}px`;

                    const insertPos =
                      coordinates?.pos ?? editor.state.doc.content.size;
                    editor
                      .chain()
                      .insertContentAt(insertPos, {
                        type: "paragraph",
                        content: [
                          { type: "image", attrs: { src: dataUrl, width } },
                        ],
                      })
                      .focus()
                      .run();
                  };
                };
                reader.readAsDataURL(file);
              });
              return true;
            }
          }
          return false;
        },
        handlePaste: (_view, event) => {
          if (!editor) {
            return false;
          }

          const files = event.clipboardData?.files;
          if (files && files.length > 0) {
            const imageFiles = Array.from(files).filter((file) =>
              file.type.startsWith("image/")
            );

            if (disableImageSupport && imageFiles.length > 0) {
              event.preventDefault();
              return true;
            }

            if (imageFiles.length > 0) {
              event.preventDefault();
              // Delegate to caller's handler so the file is uploaded to S3
              // and tracked in the attachments list.
              if (onImageFileRef.current) {
                imageFiles.forEach((file) => onImageFileRef.current!(file));
                return true;
              }
              imageFiles.forEach((file) => {
                const reader = new FileReader();
                reader.onload = (e) => {
                  if (!e.target?.result) {
                    return;
                  }
                  const dataUrl = e.target.result as string;
                  const img = new window.Image();
                  img.src = dataUrl;
                  img.onload = () => {
                    const { clientWidth } = editor.view.dom;
                    const { naturalWidth } = img;
                    const width =
                      naturalWidth > clientWidth ? "100%" : `${naturalWidth}px`;

                    editor
                      .chain()
                      .insertContent({
                        type: "paragraph",
                        content: [
                          { type: "image", attrs: { src: dataUrl, width } },
                        ],
                      })
                      .focus()
                      .run();
                  };
                };
                reader.readAsDataURL(file);
              });
              return true;
            }
          }

          const html = event.clipboardData?.getData("text/html");
          const text = event.clipboardData?.getData("text/plain");

          if (html) {
            event.preventDefault();

            const tempDiv = document.createElement("div");
            tempDiv.innerHTML = html;

            const dangerousTags = [
              "script",
              "style",
              "iframe",
              "object",
              "embed",
              "form",
              "input",
              "button",
              "select",
              "textarea",
              "meta",
            ];
            const dangerousAttrs = [
              "onclick",
              "onload",
              "onerror",
              "onmouseover",
              "onfocus",
              "onblur",
              "onchange",
              "onsubmit",
            ];

            dangerousTags.forEach((tag) => {
              const elements = tempDiv.querySelectorAll(tag);
              elements.forEach((el) => el.remove());
            });

            const allElements = tempDiv.querySelectorAll("*");
            allElements.forEach((el) => {
              dangerousAttrs.forEach((attr) => {
                el.removeAttribute(attr);
              });
            });

            const elementsWithStyles = tempDiv.querySelectorAll("[style]");
            elementsWithStyles.forEach((el) => {
              el.removeAttribute("style");
            });

            if (disableUnderline) {
              const underlineTags = tempDiv.querySelectorAll("u");
              underlineTags.forEach((el) => {
                el.replaceWith(...Array.from(el.childNodes));
              });
            }

            if (disableItalic) {
              tempDiv.querySelectorAll("em, i").forEach((el) => {
                el.replaceWith(...Array.from(el.childNodes));
              });
            }

            if (disableStrikethrough) {
              tempDiv.querySelectorAll("s, del, strike").forEach((el) => {
                el.replaceWith(...Array.from(el.childNodes));
              });
            }

            if (disableImageSupport) {
              tempDiv.querySelectorAll("img, picture").forEach((el) => {
                el.remove();
              });
            }

            // Check if the content is essentially plain text wrapped in simple tags
            // If so, use the plain text version instead to avoid unnecessary HTML
            const hasComplexFormatting = tempDiv.querySelector(
              "strong, em, b, i, u, a, ul, ol, li, h1, h2, h3, h4, h5, h6, blockquote, code, pre"
            );

            if (
              !hasComplexFormatting &&
              text &&
              text.trim() === tempDiv.textContent?.trim()
            ) {
              // Plain text content, use the text version
              editor.chain().focus().insertContent(text).run();
            } else {
              // Has formatting, use sanitized HTML
              editor.chain().focus().insertContent(tempDiv.innerHTML).run();
            }
            return true;
          }

          if (text) {
            event.preventDefault();
            editor.chain().focus().insertContent(text).run();
            return true;
          }

          return false;
        },
      },
      autofocus: false,
      content: editorHTML,
      onUpdate: ({ editor }) => {
        const html = editor.getHTML();
        setEditorHTML(html);
        if (editor.isEditable) {
          onMessageChange?.(html);
        }
        checkForLists(editor);
        updateTaggedUsers(editor);
      },
      onDestroy: () => {
        editor?.unregisterPlugin("linkBubbleMenu");
      },
      shouldRerenderOnTransaction: false,
    },
    [extensions, disableImageSupport, disableUnderline, disableItalic, disableStrikethrough]
  );

  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    editor.setEditable(editableProp);
  }, [editor, editableProp]);

  useEffect(() => {
    if (editor && onEditorReady) {
      onEditorReady(editor);
    }
  }, [editor, onEditorReady]);

  useEffect(() => {
    if (!editor) {
      return;
    }

    const updateLinkBubble = () => {
      if (isEditingLink || isLinkBubbleForcedHidden || !editor.isFocused) {
        setLinkBubble(null);
        return;
      }

      const { state } = editor;
      const { selection } = state;

      if (!selection.empty || !editor.isActive("link")) {
        setLinkBubble(null);
        return;
      }

      const $pos = selection.$from;
      const linkMark = $pos.marks().find((mark) => mark.type.name === "link");
      const href = linkMark?.attrs.href as string | undefined;

      if (!href || href.trim() === "") {
        setLinkBubble(null);
        return;
      }

      if ($pos.parentOffset === $pos.parent.nodeSize - 2) {
        setLinkBubble(null);
        return;
      }

      const linkRange = getMarkRange($pos, editor.schema.marks.link);
      const cursorCoords = editor.view.coordsAtPos(selection.from);
      const startCoords = linkRange
        ? editor.view.coordsAtPos(linkRange.from)
        : cursorCoords;
      const endCoords = linkRange
        ? editor.view.coordsAtPos(linkRange.to)
        : cursorCoords;

      const MIN_SPACE_ABOVE = 60;

      const vertical =
        cursorCoords.top >= MIN_SPACE_ABOVE
          ? { bottom: window.innerHeight - cursorCoords.top + 8 }
          : { top: cursorCoords.bottom + 8 };

      setLinkBubble({
        href,
        ...vertical,
        startLeft: startCoords.left,
        endRight: endCoords.right,
      });
    };

    const resetForcedHiddenOnBlur = () => {
      setIsLinkBubbleForcedHidden(false);
    };

    editor.on("selectionUpdate", updateLinkBubble);
    editor.on("transaction", updateLinkBubble);
    editor.on("focus", updateLinkBubble);
    editor.on("blur", updateLinkBubble);
    editor.on("blur", resetForcedHiddenOnBlur);

    return () => {
      editor.off("selectionUpdate", updateLinkBubble);
      editor.off("transaction", updateLinkBubble);
      editor.off("focus", updateLinkBubble);
      editor.off("blur", updateLinkBubble);
      editor.off("blur", resetForcedHiddenOnBlur);
    };
  }, [editor, isEditingLink, isLinkBubbleForcedHidden]);

  useEffect(() => {
    if (editor && message !== undefined) {
      const currentHtml = editor.getHTML();
      const bothEmpty =
        (!message || message === "<p></p>") &&
        (!currentHtml || currentHtml === "<p></p>");
      if (!bothEmpty && currentHtml !== message) {
        // Preserve the caret across the content sync. Plain setContent rebuilds
        // the doc and drops the caret at the end, which scrolls the editor away
        // mid-edit (e.g. while the link popup is open). Restore the prior
        // selection so syncing never moves the user's cursor.
        const wasFocused = editor.isFocused;
        const { from, to } = editor.state.selection;
        editor.commands.setContent(message);
        setEditorHTML(message);
        const docSize = editor.state.doc.content.size;
        if (wasFocused && from <= docSize) {
          editor.commands.setTextSelection({
            from: Math.min(from, docSize),
            to: Math.min(to, docSize),
          });
        }
      }
    }
  }, [editor, message]);

  const handleRemoveLink = useCallback(() => {
    if (!editor) {
      return;
    }
    const savedSelection = savedLinkSelectionRef.current;
    const chain = editor.chain().focus();
    if (savedSelection) {
      chain.setTextSelection(savedSelection);
    }
    const targetTo = savedSelection?.to ?? editor.state.selection.to;
    chain.extendMarkRange("link").unsetLink().setTextSelection(targetTo).run();
    setIsEditingLink(false);
    setIsLinkBubbleForcedHidden(false);
  }, [editor]);

  const handleEditLink = useCallback(() => {
    if (!editor) {
      return;
    }

    editor.chain().focus().extendMarkRange("link").run();

    const { from, to } = editor.state.selection;
    savedLinkSelectionRef.current = { from, to };
    const text = editor.state.doc.textBetween(from, to, " ");
    const linkAttrs = editor.getAttributes("link");

    setLinkText(text);
    setLinkUrl(linkAttrs.href);
    setLinkUrlError("");
    setIsEditingLink(true);
  }, [editor]);

  const validateUrl = useCallback(
    (url: string): { isValid: boolean; error?: string } => {
      const trimmedUrl = url.trim();

      if (!trimmedUrl) {
        return { isValid: false, error: "URL is required" };
      }

      try {
        const urlObj = new URL(trimmedUrl);

        const validProtocols = ["http:", "https:", "mailto:", "tel:", "ftp:"];
        if (!validProtocols.includes(urlObj.protocol)) {
          return {
            isValid: false,
            error:
              "Invalid protocol. Use http://, https://, mailto:, tel:, or ftp://",
          };
        }

        if (
          (urlObj.protocol === "http:" || urlObj.protocol === "https:") &&
          !urlObj.hostname
        ) {
          return { isValid: false, error: "Invalid URL format" };
        }

        return { isValid: true };
      } catch {
        let fixedUrl = trimmedUrl;

        if (!/^[a-zA-Z]+:\/\//.test(fixedUrl)) {
          fixedUrl = "https://" + fixedUrl;
        }

        try {
          new URL(fixedUrl);
          return { isValid: true };
        } catch {
          return { isValid: false, error: "Please enter a valid URL" };
        }
      }
    },
    []
  );

  const handleApplyLink = useCallback(() => {
    if (!editor) {
      return;
    }

    const url = linkUrl.trim();
    const text = linkText.trim();

    if (!url || !text) {
      handleRemoveLink();
      return;
    }
    const validation = validateUrl(url);

    if (!validation.isValid) {
      setLinkUrlError(validation.error || "Invalid URL");
      return;
    }
    setLinkUrlError("");

    let normalizedUrl = url;
    if (!/^[a-zA-Z]+:\/\//.test(normalizedUrl)) {
      normalizedUrl = "https://" + normalizedUrl;
    }
    const { from, to } = savedLinkSelectionRef.current ?? editor.state.selection;
    editor.chain().focus().setTextSelection({ from, to }).unsetLink().run();
    editor
      .chain()
      .focus()
      .insertContentAt(
        { from, to },
        {
          type: "text",
          text: text,
          marks: [{ type: "link", attrs: { href: normalizedUrl } }],
        }
      )
      .setTextSelection(from + text.length)
      .run();

    setIsEditingLink(false);
  }, [editor, linkUrl, linkText, handleRemoveLink, validateUrl]);

  const handleAttachmentsChange = useCallback(
    (newAttachments: Attachment[]) => {
      const updatedAttachments = [
        ...currentAttachmentsRef.current,
        ...newAttachments,
      ];

      currentAttachmentsRef.current = updatedAttachments;
      onAttachmentsUpdate?.(updatedAttachments);
    },
    [onAttachmentsUpdate]
  );

  useEffect(() => {
    if (onAttachmentsChangeRef.current) {
      onAttachmentsChangeRef.current(handleAttachmentsChange);
    }
  }, [handleAttachmentsChange]);

  useEffect(() => {
    if (isEditingLink && editor && editorContainerRef.current) {
      const anchor = savedLinkSelectionRef.current ?? editor.state.selection;
      const coords = editor.view.coordsAtPos(anchor.from);

      const POPUP_WIDTH = 320;
      const EDGE_BUFFER = 8;

      const clampedLeft = Math.max(
        EDGE_BUFFER,
        Math.min(coords.left, window.innerWidth - POPUP_WIDTH - EDGE_BUFFER)
      );

      setEditMenuPosition({
        top: coords.bottom + 8,
        left: clampedLeft,
      });
    }
  }, [isEditingLink, editor]);

  useEffect(() => {
    if (!isEditingLink) {
      savedLinkSelectionRef.current = null;
    }
  }, [isEditingLink]);

  const handleLinkUrlChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newUrl = e.target.value;
      setLinkUrl(newUrl);

      if (linkUrlError) {
        setLinkUrlError("");
      }
    },
    [linkUrlError]
  );

  const getLinkBubbleHorizontal = (bubble: NonNullable<typeof linkBubble>) => {
    const EDGE_BUFFER = 8;
    if (linkBubbleWidth === null) {
      return { left: Math.max(EDGE_BUFFER, bubble.startLeft) };
    }
    const fitsRight =
      bubble.startLeft + linkBubbleWidth + EDGE_BUFFER <= window.innerWidth;
    return fitsRight
      ? { left: Math.max(EDGE_BUFFER, bubble.startLeft) }
      : { right: Math.max(EDGE_BUFFER, window.innerWidth - bubble.endRight) };
  };

  if (!editor) {
    return <div>Loading editor...</div>;
  }

  return (
    <div
      ref={editorContainerRef}
      className="flex flex-col w-full h-full relative py-1"
      style={
        {
          "--placeholder-font-size": placeholderFontSize,
        } as React.CSSProperties
      }
    >
      <div className="flex-1 overflow-y-auto flex flex-col min-h-0">
        {linkBubble &&
          !isEditingLink &&
          createPortal(
            <div
              key={linkBubble.href}
              ref={measureLinkBubble}
              data-link-bubble
              onMouseDown={(e) => e.preventDefault()}
              style={{
                position: "fixed",
                ...(linkBubble.top !== undefined
                  ? { top: linkBubble.top }
                  : {}),
                ...(linkBubble.bottom !== undefined
                  ? { bottom: linkBubble.bottom }
                  : {}),
                ...getLinkBubbleHorizontal(linkBubble),
                zIndex: 9999,
              }}
              className="min-w-max max-w-[500px] flex items-center space-x-1 text-xs font-normal p-2 bg-pure-white dark:bg-verba-indigo border border-border-gray dark:border-gray-375 rounded-[4px] shadow-lg"
            >
              <span className="text-verba-indigo dark:text-verba-indigo-dk shrink-0">
                Go to link:
              </span>
              <a
                href={linkBubble.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-verba-blue dark:text-verba-blue-dk hover:underline max-w-[200px] shrink-0 truncate cursor-pointer"
                title={linkBubble.href}
                onMouseDown={(e) => {
                  e.preventDefault();
                  setIsLinkBubbleForcedHidden(true);
                  window.open(
                    linkBubble.href,
                    "_blank",
                    "noopener,noreferrer"
                  );
                }}
              >
                {linkBubble.href}
              </a>
              <span className="text-verba-indigo dark:text-verba-indigo-dk">
                |
              </span>
              <button
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleEditLink();
                }}
                className="text-verba-blue dark:text-verba-blue-dk hover:underline"
              >
                Change
              </button>
              <span className="text-verba-indigo dark:text-verba-indigo-dk">
                |
              </span>
              <button
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleRemoveLink();
                }}
                className="text-verba-blue dark:text-verba-blue-dk hover:underline"
              >
                Remove
              </button>
            </div>,
            document.body
          )}

        <div
          className={`flex-1 overflow-y-auto overflow-x-auto text-md text-heading-text dark:text-heading-text-dk flex flex-col rounded-[4px] pb-1.5 cursor-text relative w-full min-h-0 ${visibleScrollbar(
            "[&::-webkit-scrollbar]:w-[6px]"
          )} ${visibleScrollbar("[&::-webkit-scrollbar]:h-[6px]")} ${
            hasLists ? "has-lists" : ""
          }`}
          onClick={(e) => {
            if (e.target !== e.currentTarget || !editor || editor.isDestroyed) {
              return;
            }

            if (!editor.isEditable) return;
            editor.chain().focus("end").run();
          }}
        >
          <EditorContent editor={editor} />
        </div>
      </div>

      {isEditingLink &&
        editMenuPosition &&
        createPortal(
          <div
            ref={bubbleMenuRef}
            data-link-portal
            style={{
              position: "fixed",
              top: editMenuPosition.top,
              left: editMenuPosition.left,
              zIndex: 9999,
            }}
            className="bg-pure-white dark:bg-verba-indigo border border-border-gray dark:border-gray-375 rounded-[4px] shadow-lg p-4 min-w-[320px]"
          >
            <div className="space-y-2">
              <input
                type="text"
                value={linkText}
                onChange={(e) => setLinkText(e.target.value)}
                className="w-full px-2 py-1 border border-border-gray dark:border-gray-375 rounded focus:outline-none text-sm text-verba-indigo dark:text-verba-indigo-dk bg-pure-white dark:bg-verba-indigo"
                placeholder="Text"
              />
              <div className="space-y-1">
                <input
                  type="text"
                  value={linkUrl}
                  onChange={handleLinkUrlChange}
                  className={`w-full px-2 py-1 border rounded focus:outline-none text-sm text-verba-indigo dark:text-verba-indigo-dk bg-pure-white dark:bg-verba-indigo ${
                    linkUrlError
                      ? "border-verba-red-alert dark:border-verba-red-alert-dk"
                      : "border-border-gray dark:border-gray-375"
                  }`}
                  placeholder="Type or paste a link"
                />
                {linkUrlError && (
                  <div className="text-xs text-verba-red-alert dark:text-verba-red-alert-dk font-medium">
                    {linkUrlError}
                  </div>
                )}
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => {
                    setIsEditingLink(false);
                    setLinkUrlError("");
                  }}
                  className="px-4 py-1 text-sm font-medium text-verba-indigo dark:text-verba-indigo-dk bg-gray-75 dark:bg-gray-375 rounded"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApplyLink}
                  disabled={!linkUrl || !linkText || !!linkUrlError}
                  className="disabled:opacity-50 px-4 py-1 text-sm font-medium text-fixed-white dark:text-fixed-white-dk bg-verba-blue dark:bg-verba-blue-dk rounded"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default RichTextEditor;
